import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {usePageDataUrl} from '@github-ui/pull-request-page-data-tooling/use-page-data-url'
import type {MergeBoxPageData} from '../payloads/merge-box'
import {usePageDataContext} from '@github-ui/pull-request-page-data-tooling/page-data-context'
import {fetchWithTracing} from '../../helpers/fetch-with-tracing'
import {useQuery, useQueryClient, useSuspenseQuery, queryOptions} from '@github-ui/react-query'
import type {MergeMethod} from '../../types'

export function useMergeBoxPageDataQueryKey() {
  const {basePageDataUrl} = usePageDataContext()
  return [PageData.mergeBox, `basePageDataURL:${basePageDataUrl}`]
}

function useMergeBoxPageDataOptions({
  mergeMethod,
  bypassRequirements = false,
}: {
  mergeMethod: MergeMethod
  bypassRequirements?: boolean
}) {
  const searchParams = new URLSearchParams()
  searchParams.append('merge_method', mergeMethod)
  searchParams.append('bypass_requirements', bypassRequirements.toString())
  const apiURL = usePageDataUrl(PageData.mergeBox, searchParams.toString())
  const queryKey = useMergeBoxPageDataQueryKey()
  const queryClient = useQueryClient()

  return queryOptions({
    queryKey,
    queryFn: async () => {
      return fetchWithTracing<MergeBoxPageData>(apiURL, queryKey, queryClient, (data, observedAt) => {
        const observationTime = data.mergeabilityObservedAt ?? observedAt
        const previous = queryClient.getQueryData<MergeBoxPageData>(queryKey)
        const currentPull = data.pullRequest
        const previousPull = previous?.pullRequest
        const sameComparison =
          currentPull.id === previousPull?.id &&
          currentPull.headRefOid === previousPull.headRefOid &&
          currentPull.baseRefName === previousPull.baseRefName
        const previousPositiveAt =
          previous?.mergeabilityLastPositiveAt ??
          (previousPull?.mergeabilityCircuitBreakerOpen === true
            ? (previous?.mergeabilityObservedAt ?? queryClient.getQueryState(queryKey)?.dataUpdatedAt)
            : undefined)
        const isCalculating = currentPull.state === 'OPEN' && currentPull.mergeStateStatus === 'UNKNOWN'

        return {
          ...data,
          mergeabilityObservedAt: observationTime,
          mergeabilityLastPositiveAt: isCalculating
            ? currentPull.mergeabilityCircuitBreakerOpen === true
              ? observationTime
              : sameComparison
                ? previousPositiveAt
                : undefined
            : undefined,
        }
      })
    },
    staleTime: Infinity,
  })
}

export function useMergeBoxPageData({
  mergeMethod,
  bypassRequirements = false,
}: {
  mergeMethod: MergeMethod
  bypassRequirements?: boolean
}) {
  return useQuery(useMergeBoxPageDataOptions({mergeMethod, bypassRequirements}))
}

// Reads the merge box data that other components fetch, and follows their updates, without fetching it.
export function useCachedMergeBoxPageData({mergeMethod}: {mergeMethod: MergeMethod}) {
  return useQuery({...useMergeBoxPageDataOptions({mergeMethod}), enabled: false})
}

export function useSuspenseMergeBoxPageData({
  mergeMethod,
  bypassRequirements = false,
}: {
  mergeMethod: MergeMethod
  bypassRequirements?: boolean
}) {
  return useSuspenseQuery(useMergeBoxPageDataOptions({mergeMethod, bypassRequirements}))
}
